import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { type ComercialKpis, formatBRL } from '@/pages/painel/lib/comercial';

interface RealizadoResumo {
  faturamento: number;
  lucro: number;
}

export default function ComandoFinanceiro({ password }: { password: string }) {
  const [kpis, setKpis] = useState<ComercialKpis | null>(null);
  const [realizado, setRealizado] = useState<RealizadoResumo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [resumoRes, resultadoRes] = await Promise.all([
        supabase.functions.invoke('comercial-admin', { body: { action: 'resumo', password } }),
        supabase.functions.invoke('comercial-admin', { body: { action: 'resultados', password } }),
      ]);
      if (resumoRes.error) throw new Error(await extractErrorMessage(resumoRes.error));
      if (resultadoRes.error) throw new Error(await extractErrorMessage(resultadoRes.error));
      const resumo = resumoRes.data as { kpis?: ComercialKpis; error?: string };
      if (resumo?.error) throw new Error(resumo.error);
      const resultado = resultadoRes.data as { realizado?: RealizadoResumo; error?: string };
      if (resultado?.error) throw new Error(resultado.error);
      setKpis(resumo?.kpis ?? null);
      setRealizado(resultado?.realizado ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar indicadores financeiros.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const val = (v: number | null | undefined): string =>
    v == null || v === 0 ? 'Não calculável' : formatBRL(v);

  const cards = kpis
    ? [
        { label: 'Pipeline', value: val(kpis.pipeline), icon: 'ri-funds-line', tone: 'text-gray-900' },
        { label: 'Pipeline ponderado', value: val(kpis.pipeline_ponderado), icon: 'ri-scales-3-line', tone: 'text-gray-900' },
        { label: 'Lucro potencial', value: val(kpis.lucro_potencial), icon: 'ri-money-dollar-circle-line', tone: 'text-green-600' },
        { label: 'Lucro ponderado', value: val(kpis.lucro_ponderado), icon: 'ri-coins-line', tone: 'text-green-600' },
        { label: 'Faturamento realizado', value: val(realizado?.faturamento), icon: 'ri-check-double-line', tone: 'text-[#E8610A]' },
        { label: 'Lucro realizado', value: val(realizado?.lucro), icon: 'ri-verified-badge-line', tone: 'text-green-700' },
        { label: 'Previsto 30 dias', value: val(kpis.previsao_30), icon: 'ri-calendar-line', tone: 'text-gray-900' },
        { label: 'Previsto 90 dias', value: val(kpis.previsao_90), icon: 'ri-calendar-check-line', tone: 'text-gray-900' },
      ]
    : [];

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-green-50 text-green-700">
            <i className="ri-money-dollar-circle-line"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Dinheiro</h2>
            <p className="text-xs text-gray-500">Previsão de pipeline e realizado, sem misturar os dois.</p>
          </div>
        </div>
        <Link to="/painel/oportunidades" className="text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] whitespace-nowrap">
          Ver oportunidades <i className="ri-arrow-right-s-line"></i>
        </Link>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-3">{error}</div>}

      {loading && !kpis ? (
        <div className="flex justify-center py-8"><div className="w-7 h-7 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {cards.map((c) => (
            <div key={c.label} className="bg-gray-50 rounded-xl p-3">
              <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold flex items-center gap-1.5">
                <i className={c.icon}></i> {c.label}
              </p>
              <p className={`text-base font-bold truncate ${c.tone}`}>{c.value}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}